import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UsersRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg, ROLE_LABEL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserFormDialog } from "../components/users/UserFormDialog";
import { BulkUserDialog } from "../components/users/BulkUserDialog";

export default function UsersPage() {
  const { meta } = useOutletContext();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [bulk, setBulk] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (u) => {
    if (!window.confirm(`Hapus akses ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div>
          <div className="eyebrow">Administrasi</div>
          <h1 className="mt-2 font-heading font-black tracking-tight text-3xl sm:text-4xl">Pengguna & Akses</h1>
          <p className="mt-1 text-sm text-muted-foreground">Hanya email yang terdaftar di sini yang dapat masuk dengan Google.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setBulk(true)} data-testid="bulk-user-button"><UsersRound className="h-4 w-4" /> Undang Akun Polda</Button>
          <Button onClick={() => setEditing({})} data-testid="add-user-button"><Plus className="h-4 w-4" /> Daftarkan Pengguna</Button>
        </div>
      </div>
      <div className="border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left border-b bg-muted/60">
              <th className="px-4 py-2.5 eyebrow">Nama / Email</th>
              <th className="px-3 py-2.5 eyebrow">Peran</th>
              <th className="px-3 py-2.5 eyebrow">Polda</th>
              <th className="px-3 py-2.5 eyebrow">Status</th>
              <th className="px-3 py-2.5 eyebrow">Login Terakhir</th>
              <th className="px-4 py-2.5 eyebrow text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b last:border-0 hover:bg-muted/40 transition-colors" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3"><div className="font-medium">{u.name || "-"}</div><div className="text-xs text-muted-foreground font-mono">{u.email}</div></td>
                <td className="px-3 py-3">{ROLE_LABEL[u.role]}</td>
                <td className="px-3 py-3">{u.polda || "-"}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-0.5 text-[11px] font-semibold uppercase rounded-sm ${u.active ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                </td>
                <td className="px-3 py-3 text-xs text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleString("id-ID") : "Belum pernah"}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(u)} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                  {u.user_id !== me.user_id && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BulkUserDialog open={bulk} onClose={() => setBulk(false)} onSaved={load} />
      <UserFormDialog open={!!editing} user={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
